import { useEffect, useState } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, getFirestoreDb } from '../firebase/firebase';
import { UserProfile, CreateUserProfileData } from '@shared/types';
import { AppError, ErrorCodes } from '@shared/utils/errorHandler';
import { isSigningUpRef } from './useSignupFlow';

export interface UseAuthResult {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: Error | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

export function useAuthListener(
  auth: Auth,
  firestoreDb: typeof db | null
): UseAuthResult {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user: User | null) => {
        // Suppress auth state changes during signup to prevent UI flicker
        if (isSigningUpRef.current) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }
        setUser(user);
        setLoading(false);
        
        if (user && firestoreDb) {
          try {
            // Use fresh Firebase instance to avoid corruption
            const freshDb = getFirestoreDb();
            
            // Use direct Firebase approach to avoid type issues
            const userRef = doc(freshDb, 'users', user.uid);
            
            const snapshot = await getDoc(userRef);
            
            if (!snapshot.exists()) {
              setUserProfile(null);
              return;
            }
            
            const profileData = { uid: snapshot.id, ...snapshot.data() } as UserProfile;
            setUserProfile(profileData);
          } catch (err: unknown) {
            setError(err as Error);
            // Set profile to null but don't block the user
            setUserProfile(null);
          }
        } else {
          setUserProfile(null);
        }
      },
      (err: Error) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [auth, firestoreDb]);

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        // Sign them out and throw specific error
        await signOut(auth);
        const error = new AppError(
          'Please verify your email address before signing in. A verification link has been sent to your email.',
          ErrorCodes.AUTH_EMAIL_NOT_VERIFIED,
          {
            functionName: 'signIn',
            parameters: { email },
            timestamp: new Date(),
          }
        );
        setError(error);
        throw error;
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const signUp = async (email: string, password: string, name: string, phone?: string) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update the user's display name
      await updateProfile(user, { displayName: name });
      
      // Create user profile in Firestore
      if (firestoreDb) {
        const userProfileData: CreateUserProfileData = {
          name,
          email,
          role: 'player',
          signup_source: 'web',
          ...(phone && { phone }), // Only include phone if it has a value
        };
        
        
        // Use fresh Firebase instance to avoid corruption
        const freshDb = getFirestoreDb();
        const userRef = doc(freshDb, 'users', user.uid);
        await setDoc(userRef, {
          ...userProfileData,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        
        // Wait for profile to be available and update local state
        // Poll until we can successfully read the profile with all fields
        let attempts = 0;
        const maxAttempts = 20; // Increased attempts
        
        while (attempts < maxAttempts) {
          try {
            const newProfile = await getDoc(userRef);
            if (newProfile.exists()) {
              const profileData = { uid: newProfile.id, ...newProfile.data() } as UserProfile;
              // Only set profile if it has the expected role field
              if (profileData.role) {
                setUserProfile(profileData);
                break; // Success - exit loop
              }
            }
          } catch (readError) {
            // Continue polling if read fails
            console.debug('Profile read failed, retrying...', readError);
          }
          
          attempts++;
          // Wait slightly longer between attempts (150ms instead of 100ms)
          await new Promise(resolve => setTimeout(resolve, 150));
        }
        
        // If we get here, we exhausted attempts but still set the state
        // to avoid the "Complete Profile" button from appearing
        try {
          const fallbackProfile = await getDoc(userRef);
          if (fallbackProfile.exists()) {
            const profileData = { uid: fallbackProfile.id, ...fallbackProfile.data() } as UserProfile;
            setUserProfile(profileData);
          }
        } catch (fallbackError) {
          console.error('Error fetching profile after signup:', fallbackError);
        }
        
        // Send email verification
        await sendEmailVerification(user);
        
        // Sign out the user immediately after sending verification email
        await signOut(auth);
      }
    } catch (err: any) {
      
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Account creation failed. Please try again.';
      
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email address is already registered. Please use a different email or try signing in.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters long.';
      } else if (err.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email/password accounts are not enabled. Please contact support.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      const error = new Error(errorMessage);
      setError(error);
      throw error;
    }
  };

  const signOutUser = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      // Always attempt to send reset email, but don't reveal if email exists
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      // Even if there's an error (like email not found), we don't want to reveal this
      // to prevent user enumeration attacks. We'll still show success to the user.
      // Only log the actual error for debugging purposes
    }
  };

  const updateUserProfileData = async (updates: Partial<UserProfile>) => {
    if (!user || !firestoreDb) {
      throw new Error('User not authenticated or database not available');
    }

    try {
      setError(null);
      // Update in Firestore using fresh Firebase instance
      const freshDb = getFirestoreDb();
      const userRef = doc(freshDb, 'users', user.uid);
      await updateDoc(userRef, updates);
      
      // Update local state
      setUserProfile((prev: UserProfile | null) => prev ? { ...prev, ...updates } : null);
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return {
    user,
    userProfile,
    loading,
    error,
    signIn,
    signUp,
    resetPassword,
    signOut: signOutUser,
    updateUserProfile: updateUserProfileData,
  };
}
